import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ReloadIcon } from '@radix-ui/react-icons';

export function LogsTab() {
  const [activeTab, setActiveTab] = useState('system');
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = async (type: string) => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`/api/logs?type=${type}`);
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      const data = await response.json();
      setLogs(data.data);
    } catch (err) {
      setError('Failed to load logs. Please try again.');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(activeTab);
  }, [activeTab]);

  const handleRefresh = () => {
    fetchLogs(activeTab);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>System Logs</CardTitle>
            <CardDescription>View and monitor system task execution logs</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <ReloadIcon className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="system">System Tasks</TabsTrigger>
            <TabsTrigger value="cron">Cron Execution</TabsTrigger>
          </TabsList>
          
          <TabsContent value="system">
            <Card>
              <CardHeader>
                <CardTitle>System Tasks Log</CardTitle>
                <CardDescription>
                  Logs for recurring invoices, payment processing, and email sending tasks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LogDisplay content={logs} loading={loading} error={error} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="cron">
            <Card>
              <CardHeader>
                <CardTitle>Cron Execution Log</CardTitle>
                <CardDescription>
                  Cron job execution logs and scheduling information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LogDisplay content={logs} loading={loading} error={error} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function LogDisplay({ content, loading, error }: { content: string; loading: boolean; error: string }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ReloadIcon className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading logs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        {error}
      </div>
    );
  }

  if (!content) {
    return (
      <div className="p-4 text-gray-500">
        No logs available
      </div>
    );
  }

  return (
    <ScrollArea className="h-[500px] w-full rounded-md border p-4">
      <pre className="whitespace-pre-wrap font-mono text-sm">
        {content}
      </pre>
    </ScrollArea>
  );
} 